import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerAPI } from '../services/api';
import { customerTypeLabel } from '../utils/customerTypeLabel';
import toast from 'react-hot-toast';
import {
  Link2, X, RefreshCw, Loader, Sparkles, Tv, Wifi,
  Phone, MapPin, CheckCircle2, Search,
} from 'lucide-react';
import useDarkReady from '../theme/useDarkReady';

const scoreColor = (score) =>
  score >= 90 ? 'var(--cb-059669)' : score >= 70 ? 'var(--cb-2563eb)' : 'var(--cb-d97706)';

const scoreBg = (score) =>
  score >= 90 ? 'var(--cb-ecfdf5)' : score >= 70 ? 'var(--cb-eff6ff)' : 'var(--cb-fffbeb)';

function ServiceTag({ type }) {
  const catv = type === 'catv';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: '.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
      color: catv ? 'var(--primary)' : 'var(--accent)',
    }}>
      {catv ? <Tv size={11} /> : <Wifi size={11} />} {catv ? 'CableTV' : 'Broadband'}
    </span>
  );
}

function CustomerMini({ c, type }) {
  const plan = type === 'catv' ? c.catv?.package : c.broadband?.plan;
  return (
    <div style={{ flex: 1, minWidth: 220 }}>
      <ServiceTag type={type} />
      <div style={{ fontWeight: 700, fontSize: '.95rem', marginTop: 3 }}>{c.name}</div>
      <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', marginTop: 2 }}>
        {c.customerId || `#${c.billingNumber}`}
      </div>
      {c.mobile && (
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: 2 }}>
          <Phone size={11} /> {c.mobile}
        </div>
      )}
      {c.address?.line1 && (
        <div style={{ fontSize: '.78rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'flex-start', gap: 4, marginTop: 2 }}>
          <MapPin size={11} style={{ marginTop: 2, flexShrink: 0 }} />
          <span>{c.address.line1}{c.address.area ? `, ${c.address.area}` : ''}</span>
        </div>
      )}
      {plan && (
        <div style={{ fontSize: '.78rem', marginTop: 4, color: 'var(--c-374151)' }}>
          {plan.displayName || plan.name} — ₹{plan.price}
        </div>
      )}
    </div>
  );
}

// ─── One smart-suggestion row ───────────────────────────────────────────────
function SuggestionRow({ s, onLink, onDismiss, linking, selected, onToggleSelect, disabled }) {
  const busy = linking === s.key;
  return (
    <div className="card" style={{
      marginBottom: 12, borderLeft: `4px solid ${scoreColor(s.score)}`, overflow: 'hidden',
      background: selected ? 'var(--bg)' : undefined,
    }}>
      <div className="card-body" style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="checkbox"
          checked={selected}
          disabled={disabled}
          onChange={() => onToggleSelect(s.key)}
          style={{ width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
        />
        <CustomerMini c={s.catv} type="catv" />
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
          padding: '6px 10px', borderRadius: 8, background: scoreBg(s.score), flexShrink: 0,
        }}>
          <Link2 size={16} color={scoreColor(s.score)} />
          <span style={{ fontSize: '.72rem', fontWeight: 700, color: scoreColor(s.score) }}>{s.score}%</span>
        </div>
        <CustomerMini c={s.broadband} type="broadband" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 'auto' }}>
          <button className="btn btn-primary btn-sm" disabled={busy || disabled} onClick={() => onLink(s)}>
            {busy ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={12} />} Link
          </button>
          <button className="btn btn-secondary btn-sm" disabled={busy || disabled} onClick={() => onDismiss(s)}>
            <X size={12} /> Dismiss
          </button>
        </div>
      </div>
      <div style={{ padding: '0 16px 12px', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {s.reasons.map((r, i) => (
          <span key={i} style={{
            background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 20,
            padding: '2px 9px', fontSize: '.7rem', color: 'var(--c-4b5563)', fontWeight: 600,
          }}>
            {r}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Manual search-and-pick box (used twice: CATV side + Broadband side) ───
function CustomerPicker({ label, value, onChange, excludeId }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    clearTimeout(timer.current);
    setSearching(true);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await customerAPI.getAll({ search, limit: 8 });
        setResults((data.data || []).filter(c => c._id !== excludeId));
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [search, excludeId]);

  return (
    <div style={{ flex: 1, minWidth: 240, position: 'relative' }}>
      <label style={{ fontSize: '.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{label}</label>
      {value ? (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          border: '1px solid var(--border)', borderRadius: 6, padding: '8px 10px', background: 'var(--bg)',
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{value.name}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
              {value.customerId} · {value.mobile} · <span>{customerTypeLabel(value.customerType)}</span>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => onChange(null)}><X size={12} /></button>
        </div>
      ) : (
        <>
          <input
            className="form-control"
            placeholder="Search by name, mobile, or customer ID…"
            value={search}
            onChange={e => { setSearch(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            autoComplete="off"
          />
          {open && (searching || results.length > 0) && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
              background: 'var(--card-bg)', border: '1px solid var(--border)',
              borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,.12)', marginTop: 4, maxHeight: 260, overflowY: 'auto',
            }}>
              {searching ? (
                <div style={{ padding: 14, textAlign: 'center', color: 'var(--text-muted)', fontSize: '.85rem' }}>Searching…</div>
              ) : results.map(c => (
                <div key={c._id}
                  style={{ padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                  onClick={() => { onChange(c); setSearch(''); setResults([]); setOpen(false); }}
                >
                  <div style={{ fontWeight: 600, fontSize: '.85rem' }}>{c.name}</div>
                  <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>
                    {c.customerId} · {c.mobile} · <span>{customerTypeLabel(c.customerType)}</span>
                    {c.linkedAccount && <span style={{ color: 'var(--c-d97706)' }}> · already linked</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function CustomerLinkingPage() {
  useDarkReady();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [scanned, setScanned] = useState({ catv: 0, broadband: 0 });
  const [comboCount, setComboCount] = useState(0);
  const [linking, setLinking] = useState(null);
  const [autoLinking, setAutoLinking] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkLinking, setBulkLinking] = useState(false);
  const [manualA, setManualA] = useState(null);
  const [manualB, setManualB] = useState(null);
  const [manualBusy, setManualBusy] = useState(false);

  // Read-only scan — computes suggestions but never writes anything. Auto-linking the
  // exact (100%) matches is a separate, explicit action (see doAutoLink below).
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await customerAPI.getLinkSuggestions();
      const list = (data.data?.suggestions || []).map(s => ({ ...s, key: `${s.catv._id}_${s.broadband._id}` }));
      setSuggestions(list);
      setScanned(data.data?.scanned || { catv: 0, broadband: 0 });
      setComboCount(data.data?.comboCustomers ?? 0);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load suggestions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Whenever suggestions change (rescan, a link, a dismiss…), drop any selected key that
  // no longer has a matching row so the bulk toolbar's count never lies.
  useEffect(() => {
    setSelected(sel => {
      const keys = new Set(suggestions.map(s => s.key));
      const next = new Set([...sel].filter(k => keys.has(k)));
      return next.size === sel.size ? sel : next;
    });
  }, [suggestions]);

  const exactCount = suggestions.filter(s => s.score === 100).length;

  const toggleSelect = (key) => {
    setSelected(sel => {
      const next = new Set(sel);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected(sel => sel.size === suggestions.length ? new Set() : new Set(suggestions.map(s => s.key)));
  };

  // Same two-phase pattern as Payment History Import (auto-match exact rows, queue the
  // rest for review) — but triggered only by this explicit button, never on page load.
  const doAutoLink = async () => {
    setAutoLinking(true);
    try {
      const { data } = await customerAPI.autoLinkSuggestions();
      const autoLinked = data.data?.linked || [];
      if (autoLinked.length > 0) {
        setComboCount(data.data.comboCustomers);
        toast.success(`Linked ${autoLinked.length} exact match${autoLinked.length > 1 ? 'es' : ''} (100% confidence)`);
        setSuggestions(list => list.filter(s => !autoLinked.some(a => a.catvId === s.catv._id && a.broadbandId === s.broadband._id)));
      } else {
        toast('No 100% matches to link', { icon: 'ℹ️' });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Auto-link failed');
    } finally {
      setAutoLinking(false);
    }
  };

  const removeInvolving = (list, catvId, bbId) =>
    list.filter(s => s.catv._id !== catvId && s.broadband._id !== bbId);

  const doLink = async (s) => {
    setLinking(s.key);
    try {
      await customerAPI.linkAccount(s.catv._id, s.broadband._id);
      toast.success(`Linked ${s.catv.name} ↔ ${s.broadband.name}`);
      setSuggestions(list => removeInvolving(list, s.catv._id, s.broadband._id));
      setComboCount(n => n + 2);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally {
      setLinking(null);
    }
  };

  const doDismiss = (s) => {
    setSuggestions(list => list.filter(x => x.key !== s.key));
  };

  const doDismissSelected = () => {
    setSuggestions(list => list.filter(s => !selected.has(s.key)));
    setSelected(new Set());
  };

  // Links every checked suggestion one at a time (not in parallel — same volume-safety
  // reasoning as SyncImportPage's batching) and reports how many succeeded/failed.
  const doLinkSelected = async () => {
    const toLink = suggestions.filter(s => selected.has(s.key));
    if (toLink.length === 0) return;
    setBulkLinking(true);
    let linked = 0, failed = 0;
    for (const s of toLink) {
      try {
        await customerAPI.linkAccount(s.catv._id, s.broadband._id);
        linked++;
        setSuggestions(list => removeInvolving(list, s.catv._id, s.broadband._id));
      } catch {
        failed++;
      }
    }
    setComboCount(n => n + linked * 2);
    setSelected(new Set());
    setBulkLinking(false);
    if (linked > 0) toast.success(`Linked ${linked} account${linked > 1 ? 's' : ''}`);
    if (failed > 0) toast.error(`${failed} link${failed > 1 ? 's' : ''} failed`);
  };

  const doManualLink = async () => {
    if (!manualA || !manualB) return;
    setManualBusy(true);
    try {
      await customerAPI.linkAccount(manualA._id, manualB._id);
      toast.success(`Linked ${manualA.name} ↔ ${manualB.name}`);
      setSuggestions(list => removeInvolving(list, manualA._id, manualB._id));
      setComboCount(n => n + 2);
      setManualA(null); setManualB(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Link failed');
    } finally {
      setManualBusy(false);
    }
  };

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 800 }}>Link CableTV ↔ Broadband</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '.88rem' }}>
            Same household, two separate service records — link them into a combo account. Only active CableTV and Broadband customers are considered.
            Suggestions are ranked by confidence — link each one yourself, or use "Link 100% Matches" to link only the exact ones (identical mobile, name, and address) in one go.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button className="btn btn-secondary btn-sm" disabled={loading} onClick={load}>
            {loading ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <RefreshCw size={13} />} Rescan
          </button>
          <button className="btn btn-primary btn-sm" disabled={loading || autoLinking || exactCount === 0} onClick={doAutoLink}>
            {autoLinking ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <CheckCircle2 size={13} />} Link 100% Matches{exactCount > 0 ? ` (${exactCount})` : ''}
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            <Tv size={18} color="var(--primary)" />
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{scanned.catv}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Unlinked Active CableTV</div>
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            <Wifi size={18} color="var(--accent)" />
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{scanned.broadband}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Unlinked Active Broadband</div>
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            <Sparkles size={18} color="var(--c-d97706)" />
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{suggestions.length}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Suggestions</div>
          </div>
        </div>
        <div className="card" style={{ textAlign: 'center', borderTop: comboCount > 0 ? '3px solid var(--cb-10b981)' : undefined }}>
          <div className="card-body" style={{ padding: '12px 8px' }}>
            <CheckCircle2 size={18} color="var(--c-10b981)" />
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{comboCount}</div>
            <div style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>Combo Customers</div>
          </div>
        </div>
      </div>

      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <h3 style={{ fontSize: '.95rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles size={15} color="var(--c-d97706)" /> Smart Suggestions
          </h3>
          {suggestions.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '.8rem', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selected.size > 0 && selected.size === suggestions.length}
                  ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < suggestions.length; }}
                  onChange={toggleSelectAll}
                  style={{ width: 15, height: 15, cursor: 'pointer' }}
                />
                {selected.size > 0 ? `${selected.size} selected` : 'Select all'}
              </label>
              {selected.size > 0 && (
                <>
                  <button className="btn btn-primary btn-sm" disabled={bulkLinking} onClick={doLinkSelected}>
                    {bulkLinking ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={12} />} Link Selected ({selected.size})
                  </button>
                  <button className="btn btn-secondary btn-sm" disabled={bulkLinking} onClick={doDismissSelected}>
                    <X size={12} /> Dismiss Selected
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        {loading ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            <Loader size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 10 }} /><br />Scanning customers…
          </div></div>
        ) : suggestions.length === 0 ? (
          <div className="card"><div className="card-body" style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
            No matches found among active customers — use Manual Link below for anything else.
          </div></div>
        ) : (
          suggestions.map(s => (
            <SuggestionRow
              key={s.key} s={s} onLink={doLink} onDismiss={doDismiss} linking={linking}
              selected={selected.has(s.key)} onToggleSelect={toggleSelect} disabled={bulkLinking}
            />
          ))
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0, fontSize: '.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Search size={15} /> Manual Link
          </h3>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '.82rem', color: 'var(--text-muted)', marginTop: 0, marginBottom: 14 }}>
            Didn't find a match above? Search for both accounts directly.
          </p>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <CustomerPicker label="Account A" value={manualA} onChange={setManualA} excludeId={manualB?._id} />
            <CustomerPicker label="Account B" value={manualB} onChange={setManualB} excludeId={manualA?._id} />
          </div>
          <div style={{ marginTop: 14, textAlign: 'right' }}>
            <button className="btn btn-primary btn-sm" disabled={!manualA || !manualB || manualBusy} onClick={doManualLink}>
              {manualBusy ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={12} />} Link Accounts
            </button>
          </div>
        </div>
      </div>

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate('/customers')}>
          Back to Customers
        </button>
      </div>
    </div>
  );
}
